import type { V3 } from "@istari/istari-client";
import { captureException } from "@sentry/react";

import type { ChangeRequestId } from "~/change-request/model/change-request";
import {
  type ChangeRequestNotification,
  type JobNotification,
  type JobStatus,
  type NotificationStatus,
  type ReceivedResourceEvent,
  type ReceivedResourceNotification,
  type ReceivedResourceType,
  type ResourceSharedNotification,
  type SharedResourceType,
  type SharedRole,
  shortId,
  type WorkflowLogCompletedNotification,
} from "~/notifications/model/notification";
import { bucketWorkflowLogStatus } from "~/workflow-log/model/workflow-log-outcome";

export const RECEIVED_RESOURCE_EVENT_TYPES = {
  "notification.user.received_resource.shared": "shared",
  "notification.user.received_resource.updated": "updated",
  "notification.user.received_resource.revoked": "revoked",
} as const satisfies Record<string, ReceivedResourceEvent>;

export type ReceivedResourceEventData = Extract<V3.Data, { eventType: keyof typeof RECEIVED_RESOURCE_EVENT_TYPES }>;

export function reportUnrecognizedNotificationEventType(eventType: string, itemId?: string): void {
  // Direct capture rather than `reportAsyncError`, whose `fire_and_forget_label` tag would
  // mislabel a synchronous render-path check as an un-awaited async failure.
  captureException(new Error(eventType), {
    tags: { reason: "notification-unrecognized-event-type" },
    extra: itemId ? { itemId } : undefined,
  });
}

export function reportMissingEventMetadata(eventType: string, itemId: string): void {
  captureException(new Error(eventType), {
    tags: { reason: "notification-event-missing-metadata" },
    extra: { itemId },
  });
}

export type WithEventMetadata<T extends { eventId?: string; createdAt?: Date }> = T & {
  eventId: string;
  createdAt: Date;
};

// eventId/createdAt are typed optional (generated client) but the backend always sets both;
// narrow defensively rather than trust the type.
export function hasEventMetadata<T extends { eventId?: string; createdAt?: Date }>(
  data: T,
): data is WithEventMetadata<T> {
  return !!data.eventId && !!data.createdAt;
}

export function reportUnrecognizedWorkflowLogStatus(status: string, workflowLogEntryId: string): void {
  captureException(new Error(status), {
    tags: { reason: "notification-unrecognized-workflow-log-status" },
    extra: { status, workflowLogEntryId },
  });
}

export function toStatus(item: V3.InboxItemDto): NotificationStatus {
  if (item.archived) return "archived";
  if (item.read) return "read";
  return "unread";
}

export function toJobStatus(
  terminalState: V3.UserJobTerminalStateNotificationEvent["terminalState"],
): JobStatus | null {
  switch (terminalState) {
    case "Completed":
      return "completed";
    case "Failed":
      return "failed";
    case "Canceled":
      return null;
    default:
      return null;
  }
}

export function toSharedRole(permissionLevel: string): SharedRole {
  switch (permissionLevel) {
    case "editor":
    case "owner":
    case "administrator":
    case "executor":
      return "editor";
    case "viewer":
      return "viewer";
    default:
      console.error("[notifications] unknown permission level", permissionLevel);
      return "viewer";
  }
}

export function toSharedResourceType(resourceType: V3.ResourceTypeDto | null | undefined): SharedResourceType | null {
  if (!resourceType) return null;
  switch (resourceType) {
    case "artifact":
      return "artifact";
    case "model":
      return "model";
    case "file":
      return "file";
    default:
      // "system" is absent from ResourceTypeDto — systems are fetched via listSystems, not the
      // resource catalog, so access-granted events for systems don't resolve a ResourceTypeDto.
      return null;
  }
}

export function toShareNotification(
  item: V3.InboxItemDto,
  data: V3.UserAccessGrantedNotificationEvent,
  actorName: string | null,
  resource: { name: string | null; resourceType: V3.ResourceTypeDto | null; createdById?: string | null } | null,
): ResourceSharedNotification {
  const actorId = resource?.createdById ?? data.grantedUserId;
  return {
    id: item.id,
    type: "share",
    createdAt: item.created,
    status: toStatus(item),
    triggeredBy: {
      userId: actorId,
      displayName: actorName ?? "Unknown user",
    },
    payload: {
      // Falls back to "file" for unknown types including system IDs — the event payload does not
      // include resourceType, and systems resolve via listSystems rather than the resource catalog.
      // Requires backend to add resourceType to UserAccessGrantedNotificationEvent to fix routing.
      resourceType: toSharedResourceType(resource?.resourceType) ?? "file",
      resourceId: data.resourceId,
      resourceName: resource?.name ?? data.resourceId,
      role: toSharedRole(data.permissionLevel),
      shareCount: 1,
    },
  };
}

export function toJobNotification(
  item: V3.InboxItemDto,
  data: V3.UserJobTerminalStateNotificationEvent,
): JobNotification | null {
  const jobStatus = toJobStatus(data.terminalState);
  if (!jobStatus) return null;

  return {
    id: item.id,
    type: "job",
    createdAt: item.created,
    status: toStatus(item),
    triggeredBy: { userId: "system", displayName: "Build Pipeline" },
    payload: {
      jobId: data.jobId,
      jobStatus,
      functionName: `Job ${data.jobId.slice(0, 8)}`,
    },
  };
}

export function isReceivedResourceEventData(data: V3.Data): data is ReceivedResourceEventData {
  return Object.hasOwn(RECEIVED_RESOURCE_EVENT_TYPES, data.eventType);
}

export function toReceivedResourceType(resourceType: V3.ResourceTypeDto): ReceivedResourceType | null {
  switch (resourceType) {
    case "model":
      return "model";
    case "artifact":
      return "artifact";
    case "file":
      return "file";
    default:
      console.error("[notifications] unsupported received resource type", resourceType);
      return null;
  }
}

export function toReceivedResourcePayload(
  data: ReceivedResourceEventData,
): ReceivedResourceNotification["payload"] | null {
  const resourceType = toReceivedResourceType(data.resourceType);
  if (!resourceType) return null;

  return {
    event: RECEIVED_RESOURCE_EVENT_TYPES[data.eventType],
    receivingConnectionId: data.receivingConnectionId,
    receivingConnectionLabel: data.receivingConnectionLabel,
    resourceId: data.resourceId,
    resourceType,
    // `revoked` carries no file fields: the backend keeps nothing that identifies a revoked file.
    displayName: "displayName" in data ? (data.displayName ?? null) : null,
    fileExtension: "fileExtension" in data ? data.fileExtension : null,
    fileSize: "fileSize" in data ? data.fileSize : null,
    occurredAt: data.occurredAt,
  };
}

export function toReceivedResourceNotification(
  item: V3.InboxItemDto,
  data: ReceivedResourceEventData,
): ReceivedResourceNotification | null {
  const payload = toReceivedResourcePayload(data);
  if (!payload) return null;

  return {
    id: item.id,
    type: "received-resource",
    createdAt: item.created,
    status: toStatus(item),
    payload,
  };
}
export function toChangeRequestNotification(
  item: V3.InboxItemDto,
  data: V3.SystemChangeRequestOpenedNotificationEvent,
  requesterName: string | null,
  changeRequest: { title: string | null; createdById: string | null } | null,
): ChangeRequestNotification {
  return {
    id: item.id,
    type: "changeRequest",
    createdAt: item.created,
    status: toStatus(item),
    triggeredBy: {
      userId: changeRequest?.createdById ?? null,
      displayName: requesterName ?? "Unknown user",
    },
    payload: {
      changeRequestId: data.changeRequestId as ChangeRequestId,
      systemId: data.systemId,
      title: changeRequest?.title ?? shortId(data.changeRequestId),
    },
  };
}

export function toWorkflowLogNotification(
  item: V3.InboxItemDto,
  data: V3.UserWorkflowLogCompletedNotificationEvent,
  creatorName: string | null,
  entry: { title: string | null; createdById: string | null } | null,
  systemName: string | null,
): WorkflowLogCompletedNotification {
  return {
    id: item.id,
    type: "workflowLog",
    createdAt: item.created,
    status: toStatus(item),
    triggeredBy: {
      userId: entry?.createdById ?? null,
      displayName: creatorName ?? "Unknown user",
    },
    payload: {
      workflowLogEntryId: data.workflowLogEntryId,
      systemId: data.systemId,
      systemName,
      status: data.status,
      outcome: bucketWorkflowLogStatus(data.status),
      title: entry?.title ?? shortId(data.workflowLogEntryId),
    },
  };
}
